import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { cn } from "@/shared/lib/cn";

export type SliderProps = Omit<
  ComponentPropsWithoutRef<typeof SliderPrimitive.Root>,
  "children"
> & {
  trackClassName?: string;
  rangeClassName?: string;
  thumbClassName?: string;
};

/**
 * Slider - Single thumb or multi-thumb slider (Radix Slider).
 * Thumb hit area ≥ `--touch-target-min` for Field glove / mobile use.
 */
export const Slider = forwardRef<
  ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(function Slider(
  { className, trackClassName, rangeClassName, thumbClassName, ...props },
  ref,
) {
  const thumbCount = props.value?.length ?? props.defaultValue?.length ?? 1;

  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        "relative flex w-full touch-none select-none items-center",
        "min-h-[var(--touch-target-min)]",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        className={cn(
          "relative h-1.5 w-full grow overflow-hidden rounded-full bg-border",
          trackClassName,
        )}
      >
        <SliderPrimitive.Range
          className={cn("absolute h-full bg-primary", rangeClassName)}
        />
      </SliderPrimitive.Track>
      {Array.from({ length: thumbCount }, (_, i) => (
        <SliderPrimitive.Thumb
          key={i}
          className={cn(
            "relative block size-5 shrink-0 rounded-full border-2 border-primary bg-surface shadow-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]",
            "disabled:pointer-events-none hover:scale-110 active:scale-95 transition-transform cursor-grab active:cursor-grabbing",
            /* Expand hit area to ≥44px without changing visual size. */
            'after:absolute after:inset-[-0.625rem] after:content-[""]',
            thumbClassName,
          )}
        />
      ))}
    </SliderPrimitive.Root>
  );
});

export interface RangeSliderProps
  extends Omit<SliderProps, "value" | "defaultValue" | "onValueChange"> {
  /** Dual-thumb range values [min, max] */
  value?: [number, number];
  defaultValue?: [number, number];
  onValueChange?: (value: [number, number]) => void;
  minStepsBetweenThumbs?: number;
}

/**
 * RangeSlider - Dual-thumb slider for selecting numeric ranges [Min, Max].
 * Ideal for temperature ranges, price intervals, tolerance bands.
 */
export const RangeSlider = forwardRef<
  ElementRef<typeof SliderPrimitive.Root>,
  RangeSliderProps
>(function RangeSlider(
  { value, defaultValue = [0, 100], onValueChange, minStepsBetweenThumbs = 1, ...props },
  ref,
) {
  return (
    <Slider
      ref={ref}
      value={value}
      defaultValue={defaultValue}
      minStepsBetweenThumbs={minStepsBetweenThumbs}
      onValueChange={(vals) => {
        if (vals.length >= 2) {
          onValueChange?.([vals[0]!, vals[1]!]);
        }
      }}
      {...props}
    />
  );
});
